import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./Landing.css";
import { Margin } from "@mui/icons-material";

interface LandingProps {
  onLogin: () => void;
}

const Landing: React.FC<LandingProps> = ({ onLogin }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("is-loaded");
    setIsLoaded(true);
    return () => document.documentElement.classList.remove("is-loaded");
  }, []);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="landing">
      {/* NAVBAR */}
      <header className="nav">
        <div className="nav-container">
          <motion.div
            className="brand"
            onClick={() => scrollToId("hero")}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="brand-text">Fin Titles</span>
            <span className="beta-badge">BETA</span>
          </motion.div>

          <motion.div
            className="nav-actions"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <button className="btn btn-ghost" onClick={() => scrollToId("features")}>
              Features
            </button>
            <button className="btn btn-ghost" onClick={() => scrollToId("pricing")}>
              Pricing
            </button>
            <button className="btn btn-primary" onClick={onLogin}>
              Get Started
            </button>
          </motion.div>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section id="hero" className="hero">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">Cinematic End Credits, Simplified</h1>
            <p className="hero-subtitle">
              Auto-format from spreadsheets. Export in minutes. Built for indie filmmakers, students, and small studios.
            </p>
            <motion.div
              className="hero-actions"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <button className="btn btn-primary btn-xl" onClick={onLogin}>
                Let the magic begin!
              </button>
            </motion.div>
          </motion.div>
        </section>

        {/* FEATURES */}
        <section id="features" className="section">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Why Choose Fin Titles?
          </motion.h2>

          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            viewport={{ once: true }}
          >
            We make professional credits as easy as dragging a file.
          </motion.p>

          <div className="grid">
            {[
              { title: "📊 Smart Auto-formatting", desc: "Import Excel or Google Sheets and instantly map cast & crew into clean, cinematic credits." },
              { title: "⚡ Real-time Preview", desc: "Adjust layout and see results instantly — no re-rendering required." },
              { title: "🎨 Custom Styling", desc: "Bring your own fonts, colors, and alignment for a unique look that matches your project." },
              { title: "🌍 Global Support", desc: "RTL-ready with full Unicode support for Arabic, Hebrew, and more." },
              { title: "🎬 Export Options", desc: "Export as MP4, HTML, or transparent MOV — ready for your NLE workflow." },
              { title: "⚙️ Precision Controls", desc: "Fine-tune timing, spacing, and transitions with pro-level controls." },
              { title: "🖋 Advanced Typography", desc: "Control fonts, spacing, and kerning to make credits look perfect on screen." },
              { title: "🔄 Seamless Updates", desc: "Sync changes instantly from your spreadsheet without restarting the app." },
            ].map((f, i) => (
              <motion.div
                className="card"
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.04 }}
              >
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="section">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Simple, Transparent Pricing
          </motion.h2>

          <p className="section-subtitle">Start free, upgrade as you grow.</p>

          <div className="pricing-grid">
            {[
              { name: "Free", price: "Free", features: ["720p export with watermark", "Basic templates", "Community support"], marginTop: "27px" },
              { name: "Pro", price: "$10 / month", features: ["Full HD export", "No watermark", "Custom templates", "Unlimited projects"] },
              { name: "Studio", price: "$35 / month", features: ["Transparent MOV export", "Team collaboration", "Priority support"], marginTop: "27px" },
            ].map((plan, i) => (
              <motion.div
                className="pricing-card"
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * i, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <h3>{plan.name}</h3>
                <p className="price">{plan.price}</p>
                <ul>{plan.features.map((f, j) => <li key={j}>{f}</li>)}</ul>
                <button className="btn btn-outline" style={{ marginTop: plan.marginTop }} onClick={onLogin}>
                  {plan.name === "Studio" ? "Contact Sales" : `Choose ${plan.name}`}
                </button>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="footer">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          © {new Date().getFullYear()} Fin Titles — All rights reserved.
        </motion.p>
      </footer>
    </div>
  );
};

export default Landing;
